import { useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const usePaginationControls = ({ initialTotalItems, initialPage = 1, initialSize = 25 }) => {
  const { t } = useTranslation();
  const [paginationState, setPaginationState] = useState({
    page: initialPage,
    size: initialSize,
    total: initialTotalItems,
  });

  const showingLabel = useMemo(() => {
    const { page: currentPage, size: pageSize, total: totalItems } = paginationState;

    const start = (currentPage - 1) * pageSize + 1;
    let end = totalItems;

    if (pageSize < totalItems) {
      end = pageSize * currentPage;
      if (end > totalItems) end = totalItems;
    }

    return `${t('pagination_controls.showing', {
      start,
      end,
      total: totalItems,
    })}`;
  }, [paginationState, t]);

  const labels = {
    first: t('pagination_controls.first'),
    last: t('pagination_controls.last'),
    navigation: t('pagination_controls.navigation'),
    next: t('pagination_controls.next'),
    previous: t('pagination_controls.previous'),
    showing: showingLabel,
    showPerPage: t('pagination_controls.show_per_page'),
  };

  const getAriaLabel = useCallback(
    ({ type, page, selected }) => {
      if (type === 'page') {
        return selected
          ? ''
          : `${t('pagination_controls.go_to_page', {
              page,
            })}`;
      }

      return `${t('pagination_controls.go_to_type_page', {
        type,
      })}`;
    },
    [t]
  );

  const onPageChange = useCallback((_, page, size) => {
    setPaginationState(prevPaginationState => ({
      ...prevPaginationState,
      page,
      size,
    }));
  }, []);

  const paginationProps = {
    labels,
    onPageChange,
    getAriaLabel,
  };

  const setTotalItems = useCallback(total => {
    setPaginationState(prevPaginationState => ({
      ...prevPaginationState,
      total,
    }));
  }, []);

  return { paginationState, paginationProps, setTotalItems };
};

export default usePaginationControls;
